import { useMantineTheme } from '@mantine/core';
import type {
	EntityPolicyOut,
	PolicyFramework,
	PolicyOut,
} from '@repo/api-codegen';
import MultiSelector from '@repo/common/components/MultiSelector/MultiSelector';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import { IconButton } from '@repo/foundations';
import type { IconNames } from '@repo/foundations/components/Icon/types';
import type { SecodaThemeShades } from '@repo/theme/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthUser } from '../../../api';
import { usePolicyWritePermission } from '../../../api/hooks/policy';
import { useUpdatePolicy } from '../hooks';

const DQS_FRAMEWORK = 'DQS' as PolicyFramework;

interface FrameworkSelectorProps {
	policy: PolicyOut | EntityPolicyOut;
	readOnly?: boolean;
	hideOnEmpty?: boolean;
	placeholder?: string;
	placeholderIconName?: IconNames;
	iconOnly?: boolean;
}

export function FrameworkSelector({
	policy,
	readOnly = false,
	hideOnEmpty = true,
	placeholder,
	placeholderIconName,
	iconOnly = false,
}: FrameworkSelectorProps) {
	const { user, isViewerUser } = useAuthUser();
	const hasPermission = usePolicyWritePermission();
	const theme = useMantineTheme();
	const [selected, setSelected] = useState<PolicyFramework[]>(
		policy.frameworks ?? []
	);

	useEffect(() => {
		setSelected(policy.frameworks ?? []);
	}, [policy.frameworks]);

	const defaultFrameworkOptions = useMemo(
		() =>
			SELECTABLE_PROPERTY_OPTIONS.complianceFramework.map((item) => ({
				value: item.value,
				label: item.label,
				color: theme.other.getColor(item.color) as SecodaThemeShades, // tag type multiselectors expect tag properties which are saved with colors rather than names
			})),
		[theme]
	);

	const dqsFrameworkOption = useMemo(
		() => ({
			value: DQS_FRAMEWORK,
			label: DQS_FRAMEWORK,
			color: theme.other.getColor(
				'icon/decorative/violet'
			) as SecodaThemeShades,
		}),
		[theme]
	);

	const frameworkOptions = useMemo(() => {
		if (
			(policy.frameworks?.includes(DQS_FRAMEWORK) ?? false) &&
			!defaultFrameworkOptions.some((item) => item.value === DQS_FRAMEWORK)
		) {
			return [...defaultFrameworkOptions, dqsFrameworkOption];
		}
		return defaultFrameworkOptions;
	}, [defaultFrameworkOptions, dqsFrameworkOption, policy.frameworks]);

	const { mutateAsync: updatePolicy } = useUpdatePolicy({});
	const handleChange = useCallback(
		(newFrameworks: string[]) => {
			updatePolicy({
				pathParams: {
					policyId: policy.id,
				},
				body: {
					frameworks: newFrameworks as PolicyFramework[],
				},
			});
			setSelected(newFrameworks as PolicyFramework[]);
		},
		[policy.id, updatePolicy]
	);

	return (
		<MultiSelector
			hideOnEmpty={hideOnEmpty}
			readOnly={readOnly || !hasPermission}
			initialSelected={selected}
			placeholder={placeholder ? placeholder : 'Frameworks'}
			placeholderIconName={placeholderIconName}
			property="frameworks"
			iconType="tag"
			options={frameworkOptions}
			permittedId={user.id}
			isViewerUser={isViewerUser}
			isMenuItemBadge={false}
			onChange={handleChange as (newValue: (string | boolean)[]) => void}
			target={
				iconOnly ? <IconButton iconName="plus" variant="tertiary" /> : undefined
			}
		/>
	);
}
